import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState, useEffect } from 'react';

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      image: 'https://customer-assets.emergentagent.com/job_vineyardelegance/artifacts/i5m2h9iz_Y.jpeg',
      title: 'Vení a descubrir esta',
      subtitle: 'Tierra de Vinos'
    },
    {
      image: 'https://customer-assets.emergentagent.com/job_vineyardelegance/artifacts/w65wtk47_Fondo1.jpg',
      title: 'Tradición vinícola desde',
      subtitle: '4 Generaciones'
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="relative h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image Carousel */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          className="absolute inset-0 z-0"
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 1.2 }}
        >
          <div 
            className="w-full h-full bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url(${slides[currentSlide].image})`
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60" />
        </motion.div>
      </AnimatePresence>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-4 z-20 text-white/80 hover:text-white transition-colors p-2 rounded-full bg-black/20 hover:bg-black/40"
        aria-label="Previous slide"
      >
        <ChevronLeft className="h-8 w-8" />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-4 z-20 text-white/80 hover:text-white transition-colors p-2 rounded-full bg-black/20 hover:bg-black/40"
        aria-label="Next slide"
      >
        <ChevronRight className="h-8 w-8" />
      </button>

      {/* Slide Indicators */}
      <div className="absolute bottom-24 left-1/2 transform -translate-x-1/2 z-20 flex gap-2">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`h-2 rounded-full transition-all duration-300 ${
              index === currentSlide ? 'w-8' : 'w-2 bg-white/50'
            }`}
            style={index === currentSlide ? {background: '#D4AF37'} : {}}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.8 }}
          >
            <motion.h1 
              className="text-5xl md:text-7xl font-bold text-white mb-6"
            >
              {slides[currentSlide].title}
              <span className="block" style={{color: '#D4AF37'}}>{slides[currentSlide].subtitle}</span>
            </motion.h1>
          
          <motion.p 
            className="text-xl md:text-2xl text-gray-200 mb-8 max-w-3xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.8 }}
          >
            Bodega turística y WineRestaurant a pocos minutos del centro de Montevideo
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.8 }}
          >
            <motion.button
              onClick={() => scrollToSection('tours')}
              className="text-white px-8 py-4 rounded-full text-lg font-bold transition-all shadow-2xl"
              style={{background: 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)'}}
              whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(212, 175, 55, 0.4)" }}
              whileTap={{ scale: 0.95 }}
              data-testid="hero-explorar-btn"
            >
              Explorar Tours
            </motion.button>
            <motion.button
              onClick={() => scrollToSection('reservation')}
              className="bg-white/10 backdrop-blur-md text-white border-2 px-8 py-4 rounded-full text-lg font-semibold transition-all"
              style={{borderColor: '#D4AF37'}}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              data-testid="hero-reservar-btn"
            >
              Hacer Reserva
            </motion.button>
          </motion.div>
        </motion.div>
      </AnimatePresence>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
      >
        <button
          onClick={() => scrollToSection('about')}
          className="text-white opacity-75 hover:opacity-100 transition-opacity"
          aria-label="Scroll down"
        >
          <ChevronDown className="h-10 w-10" />
        </button>
      </motion.div>
    </section>
  );
};

export default Hero;
